import { useState } from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { FormSubmitButton } from "~/components/form-fields/FormSubmitButton";
import { FormErrorSummary } from "~/components/form-fields/FormErrorSummary";
import { ValidatedInput } from "~/components/form-fields/ValidatedInput";
import { Button } from "~/components/ui/button";
import { claimImportedAccount } from "~/features/auth/auth.mutations";
import { AppleIcon, GoogleIcon, MicrosoftIcon } from "~/components/ui/icons";
import { auth } from "~/lib/auth-client";
import { useAppForm } from "~/lib/hooks/useAppForm";
import { useFocusWhen } from "~/lib/hooks/use-focus-when";
import { useHydrated } from "~/lib/hooks/use-hydrated";
import { getBrand } from "~/tenant";
import {
  getOAuthErrorMessage,
  settleAsyncResult,
  settleAuthClientResult,
} from "../auth-client-utils";
import { getFriendlyAuthErrorMessage } from "../auth-error-messages";
import {
  buildLoginHref,
  decodePostAuthIntent,
  normalizeAuthRedirectPath,
  resolvePostAuthRedirectPath,
} from "../auth-routing";
import { lookupAccountRecoveryByEmail, type SocialAuthProvider } from "../auth.queries";
import { signupFormFields, signupFormSchema } from "../auth.schemas";
import { useAuthSuccessHandler } from "../hooks/use-auth-success-handler";
import { AuthDivider } from "./auth-divider";
import { AuthPageFooter } from "./auth-page-footer";
import { AuthPageHeader } from "./auth-page-header";

type SignupFormProps = {
  socialProviders?: SocialAuthProvider[];
  redirectPath?: string | undefined;
  postAuthIntent?: string | undefined;
  oauthRedirectPath?: string | undefined;
  onBeforeOAuth?: () => void;
  onAuthenticated?: () => void;
  heading?: string | undefined;
};

export default function SignupForm({
  socialProviders,
  redirectPath,
  postAuthIntent: postAuthIntentParam,
  oauthRedirectPath,
  onBeforeOAuth,
  onAuthenticated,
  heading,
}: SignupFormProps) {
  const { i18n } = useLingui();
  const brand = getBrand();
  const postAuthIntent = decodePostAuthIntent(postAuthIntentParam);
  const redirectUrl = resolvePostAuthRedirectPath(
    redirectPath,
    "/dashboard",
    postAuthIntentParam,
  );
  const requestedRedirectPath =
    normalizeAuthRedirectPath(redirectPath) ?? postAuthIntent?.returnTo;
  const safeOauthRedirectPath = resolvePostAuthRedirectPath(
    oauthRedirectPath,
    redirectUrl,
    postAuthIntentParam,
  );
  const loginHref = buildLoginHref(requestedRedirectPath, postAuthIntent);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isHydrated = useHydrated();
  const nameInputRef = useFocusWhen<HTMLInputElement>({
    enabled: true,
    delayMs: 25,
  });
  const handleAuthSuccess = useAuthSuccessHandler({
    onAuthenticated,
    redirectTo: redirectUrl,
  });

  const claimImportedAccountIfNeeded = async (params: {
    email: string;
    password: string;
  }) => {
    const { data: recovery } = await settleAsyncResult(
      () =>
        lookupAccountRecoveryByEmail({
          data: {
            email: params.email,
          },
        }),
      i18n._(msg`Unable to check account recovery right now.`),
    );

    if (
      !(
        recovery?.match === "user" &&
        recovery.isTopscoreImported &&
        !recovery.hasPasswordAccount
      )
    ) {
      return false;
    }

    const { errorMessage: claimError } = await settleAsyncResult(
      () =>
        claimImportedAccount({
          data: {
            email: params.email,
            newPassword: params.password,
          },
        }),
      i18n._(msg`Unable to claim this imported account right now.`),
    );

    if (claimError) {
      setErrorMessage(claimError);
      return true;
    }

    const { errorMessage: claimedLoginError } = await settleAuthClientResult(
      () =>
        auth.signIn.email({
          email: params.email,
          password: params.password,
        }),
      i18n._(msg`Unable to sign in with your new password.`),
    );

    if (claimedLoginError) {
      setErrorMessage(claimedLoginError);
      return true;
    }

    await handleAuthSuccess();
    return true;
  };

  const availableProviders = socialProviders ?? ["google"];
  const providerConfig: Record<
    SocialAuthProvider,
    { label: string; Icon: typeof GoogleIcon }
  > = {
    google: { label: i18n._(msg`Google`), Icon: GoogleIcon },
    microsoft: { label: i18n._(msg`Microsoft`), Icon: MicrosoftIcon },
    apple: { label: i18n._(msg`Apple`), Icon: AppleIcon },
  };

  const form = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: async ({ value }) => {
      setIsLoading(true);
      setErrorMessage("");

      const validation = signupFormSchema.safeParse(value);
      if (!validation.success) {
        setErrorMessage(
          validation.error.issues[0]?.message ?? i18n._(msg`Signup failed`),
        );
        setIsLoading(false);
        return;
      }

      const normalizedEmail = value.email.trim().toLowerCase();

      if (
        await claimImportedAccountIfNeeded({
          email: normalizedEmail,
          password: value.password,
        })
      ) {
        setIsLoading(false);
        return;
      }

      const { errorMessage: signupError } = await settleAuthClientResult(
        () =>
          auth.signUp.email({
            name: value.name,
            email: normalizedEmail,
            password: value.password,
            callbackURL: redirectUrl,
          }),
        i18n._(msg`Signup failed`),
      );

      if (signupError) {
        const normalizedError = signupError.trim().toLowerCase();
        if (
          normalizedError.includes("user already exists") ||
          normalizedError.includes("account already exists") ||
          normalizedError.includes("failed to create user")
        ) {
          if (
            await claimImportedAccountIfNeeded({
              email: normalizedEmail,
              password: value.password,
            })
          ) {
            setIsLoading(false);
            return;
          }
        }

        setErrorMessage(
          getFriendlyAuthErrorMessage(signupError, i18n._(msg`Signup failed`), "signup"),
        );
        form.reset({
          name: value.name,
          email: value.email,
          password: value.password,
          confirmPassword: value.confirmPassword,
        });
        setIsLoading(false);
        return;
      }

      await handleAuthSuccess();
      setIsLoading(false);
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <form
        data-testid="signup-form"
        data-hydrated={isHydrated ? "true" : "false"}
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <div className="flex flex-col gap-6">
          <AuthPageHeader
            eyebrow={i18n._(msg`Create account`)}
            title={heading ?? i18n._(msg`Create your account`)}
            description={i18n._(
              msg`Set up your ${brand.name} sign-in first. We'll guide you through the remaining onboarding details right after.`,
            )}
          />

          <div className="flex flex-col gap-5">
            <form.AppField
              name="name"
              validators={{
                onChange: signupFormFields.name,
              }}
            >
              {(field) => (
                <ValidatedInput
                  field={field}
                  inputRef={nameInputRef}
                  label={i18n._(msg`Name`)}
                  type="text"
                  placeholder={i18n._(msg`John Doe`)}
                  autoComplete="name"
                />
              )}
            </form.AppField>

            <form.AppField
              name="email"
              validators={{
                onChange: signupFormFields.email,
              }}
            >
              {(field) => (
                <ValidatedInput
                  field={field}
                  label={i18n._(msg`Email`)}
                  type="email"
                  placeholder={i18n._(msg`hello@example.com`)}
                  autoComplete="email"
                />
              )}
            </form.AppField>

            <form.AppField
              name="password"
              validators={{
                onChange: signupFormFields.password,
              }}
            >
              {(field) => (
                <ValidatedInput
                  field={field}
                  label={i18n._(msg`Password`)}
                  type="password"
                  placeholder={i18n._(msg`Password`)}
                  autoComplete="new-password"
                />
              )}
            </form.AppField>

            <form.AppField
              name="confirmPassword"
              validators={{
                onChangeListenTo: ["password"],
                onChange: ({ value, fieldApi }) => {
                  const password = fieldApi.form.getFieldValue("password");
                  if (value && password && value !== password) {
                    return i18n._(msg`Passwords do not match`);
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <ValidatedInput
                  field={field}
                  label={i18n._(msg`Confirm Password`)}
                  type="password"
                  placeholder={i18n._(msg`Confirm Password`)}
                  autoComplete="new-password"
                />
              )}
            </form.AppField>

            <FormSubmitButton
              isSubmitting={form.state.isSubmitting || isLoading}
              className="mt-2 w-full"
              size="lg"
              loadingText={i18n._(msg`Signing up...`)}
            >
              {i18n._(msg`Sign up`)}
            </FormSubmitButton>
          </div>

          {errorMessage ? <FormErrorSummary errors={[errorMessage]} /> : null}

          {availableProviders.length > 0 ? (
            <>
              <AuthDivider />

              <div className="space-y-2">
                {availableProviders.map((providerId) => {
                  const config = providerConfig[providerId];
                  if (!config) {
                    return null;
                  }

                  const { Icon, label } = config;
                  return (
                    <Button
                      key={providerId}
                      variant="outline"
                      className="w-full"
                      type="button"
                      disabled={isLoading || form.state.isSubmitting}
                      onClick={() => {
                        onBeforeOAuth?.();
                        return auth.signInWithOAuth(
                          {
                            provider: providerId,
                            callbackURL: safeOauthRedirectPath,
                          },
                          {
                            onRequest: () => {
                              setIsLoading(true);
                              setErrorMessage("");
                            },
                            onError: (context) => {
                              setIsLoading(false);
                              setErrorMessage(
                                getOAuthErrorMessage(
                                  context,
                                  i18n._(msg`OAuth signup failed`),
                                ),
                              );
                            },
                          },
                        );
                      }}
                    >
                      <Icon />
                      {i18n._(msg`Sign up with ${label}`)}
                    </Button>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </form>

      <AuthPageFooter
        prompt={i18n._(msg`Already have an account?`)}
        to={loginHref}
        label={i18n._(msg`Login`)}
      />
    </div>
  );
}
